import { useEffect, useState } from "react";
import { COLORS, Ic, ICONS } from "@/lib/theme";
import { apiGet, apiPost, apiPut, apiDelete, formatApiError } from "@/lib/api";

const emptyFamily = { first_name: "", surname: "", hebrew_name: "", cell: "", email: "", relationship: "", date_of_death: "" };

export default function Profile({ onClose }) {
  const [profile, setProfile] = useState(null);
  const [family, setFamily] = useState([]);
  const [familyForm, setFamilyForm] = useState(emptyFamily);
  const [editingId, setEditingId] = useState(null);
  const [msg, setMsg] = useState("");
  const [error, setError] = useState("");

  const load = async () => {
    const [p, f] = await Promise.all([apiGet("/profile"), apiGet("/profile/family")]);
    setProfile(p.profile);
    setFamily(f.family || []);
  };
  useEffect(() => { load().catch((e) => setError(formatApiError(e))); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setP = (k) => (e) => setProfile({ ...profile, [k]: e.target.value });
  const setF = (k) => (e) => setFamilyForm({ ...familyForm, [k]: e.target.value });

  const saveProfile = async () => {
    setError(""); setMsg("");
    try {
      const r = await apiPut("/profile", {
        first_name: profile.first_name, last_name: profile.last_name, hebrew_name: profile.hebrew_name || null,
        family_name: profile.family_name || null, phone: profile.phone || null, whatsapp: profile.whatsapp || null, address: profile.address || null,
      });
      setProfile(r.profile); setMsg("Profile saved.");
    } catch (e) { setError(formatApiError(e)); }
  };

  const saveFamily = async () => {
    setError(""); setMsg("");
    if (!familyForm.first_name || !familyForm.surname) { setError("Name and surname are required."); return; }
    try {
      if (editingId) await apiPut(`/profile/family/${editingId}`, familyForm);
      else await apiPost("/profile/family", familyForm);
      setFamilyForm(emptyFamily); setEditingId(null); await load(); setMsg("Family member saved.");
    } catch (e) { setError(formatApiError(e)); }
  };

  const editFamily = (m) => {
    setEditingId(m.id);
    setFamilyForm({ ...emptyFamily, ...m });
  };
  const removeFamily = async (id) => {
    if (!window.confirm("Remove this family member?")) return;
    await apiDelete(`/profile/family/${id}`); await load();
  };
  const invite = async (id) => {
    setError(""); setMsg("");
    try { const r = await apiPost(`/profile/family/${id}/invite`); setMsg(r.email_sent ? "Invitation email sent." : "Invite created, but SMTP email is not configured on the server."); }
    catch (e) { setError(formatApiError(e)); }
  };

  return (
    <div className="anim-slideIn" style={{ position: "absolute", inset: 0, background: COLORS.cream, zIndex: 45, display: "flex", flexDirection: "column" }} data-testid="profile-screen">
      <div className="ls-header" style={{ padding: "16px 20px 18px" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <button onClick={onClose} aria-label="Back" className="no-tap-highlight" style={{ background: "none", border: "none", padding: 0, cursor: "pointer" }}><Ic d={ICONS.back} color="#fff" size={26} /></button>
          <div><div className="font-heading" style={{ color: "#fff", fontWeight: 800, fontSize: 20 }}>My Profile & Family</div><div style={{ color: "rgba(255,255,255,.75)", fontSize: 12, fontWeight: 700 }}>Keep your member and family details current</div></div>
        </div>
      </div>
      <div className="ls-scroll" style={{ flex: 1, overflowY: "auto", padding: "18px 18px 40px" }}>
        {error && <div style={{ marginBottom: 12, padding: 12, borderRadius: 12, background: "#fff0f3", color: "#a02040", fontWeight: 700, fontSize: 13 }}>{error}</div>}
        {msg && <div style={{ marginBottom: 12, padding: 12, borderRadius: 12, background: "#edf9f0", color: "#26713d", fontWeight: 700, fontSize: 13 }}>{msg}</div>}
        {profile && <div className="ls-card" style={{ padding: 16, marginBottom: 18 }}>
          <div className="font-heading" style={{ fontWeight: 800, fontSize: 19, color: COLORS.ink, marginBottom: 12 }}>Member Profile</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <input className="ls-input" placeholder="Name" value={profile.first_name || ""} onChange={setP("first_name")} />
            <input className="ls-input" placeholder="Surname" value={profile.last_name || ""} onChange={setP("last_name")} />
          </div>
          <input className="ls-input" style={{ marginTop: 10 }} placeholder="Hebrew name" value={profile.hebrew_name || ""} onChange={setP("hebrew_name")} />
          <input className="ls-input" style={{ marginTop: 10 }} placeholder="Family name" value={profile.family_name || ""} onChange={setP("family_name")} />
          <input className="ls-input" style={{ marginTop: 10 }} placeholder="Cell" value={profile.phone || ""} onChange={setP("phone")} />
          <input className="ls-input" style={{ marginTop: 10 }} placeholder="WhatsApp" value={profile.whatsapp || ""} onChange={setP("whatsapp")} />
          <input className="ls-input" style={{ marginTop: 10 }} placeholder="Address" value={profile.address || ""} onChange={setP("address")} />
          <div style={{ marginTop: 10, fontSize: 12.5, color: COLORS.muted, fontWeight: 700 }}>Email: {profile.email} {profile.email_verified ? "✓ verified" : "· verification pending"}</div>
          <button onClick={saveProfile} className="ls-primary-btn" style={{ width: "100%", marginTop: 14 }}>Save Profile</button>
        </div>}

        <div className="font-heading" style={{ fontWeight: 800, fontSize: 20, color: COLORS.ink, margin: "4px 0 10px" }}>Family Tree</div>
        {family.length === 0 && <div style={{ color: COLORS.muted, fontWeight: 600, fontSize: 13, marginBottom: 12 }}>No family members added yet.</div>}
        <div style={{ display: "grid", gap: 10, marginBottom: 18 }}>
          {family.map((m) => <div key={m.id} className="ls-card" style={{ padding: 14 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <div style={{ width: 42, height: 42, borderRadius: "50%", background: COLORS.lilac, display: "flex", alignItems: "center", justifyContent: "center" }}><Ic d={ICONS.people} color={COLORS.plum} size={21} /></div>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 800 }}>{m.first_name} {m.surname}</div>
                {m.hebrew_name && <div style={{ fontSize: 12.5, color: COLORS.purple, fontWeight: 700 }}>{m.hebrew_name}</div>}
                <div style={{ fontSize: 12, color: COLORS.muted, lineHeight: 1.5, marginTop: 3 }}>{[m.relationship, m.cell, m.email, m.date_of_death ? `Date of death: ${m.date_of_death}` : null].filter(Boolean).join(" · ")}</div>
              </div>
            </div>
            <div style={{ display: "flex", gap: 8, marginTop: 10, flexWrap: "wrap" }}>
              <button onClick={() => editFamily(m)} className="ls-outline-btn">Edit</button>
              {m.email && <button onClick={() => invite(m.id)} className="ls-outline-btn">Send Invite</button>}
              <button onClick={() => removeFamily(m.id)} className="ls-outline-btn" style={{ color: "#a02040" }}>Remove</button>
            </div>
          </div>)}
        </div>

        <div className="ls-card" style={{ padding: 16 }}>
          <div className="font-heading" style={{ fontWeight: 800, fontSize: 18, color: COLORS.ink, marginBottom: 10 }}>{editingId ? "Edit family member" : "Add family member"}</div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
            <input className="ls-input" placeholder="Name" value={familyForm.first_name} onChange={setF("first_name")} />
            <input className="ls-input" placeholder="Surname" value={familyForm.surname} onChange={setF("surname")} />
          </div>
          <input className="ls-input" style={{ marginTop: 10 }} placeholder="Hebrew name" value={familyForm.hebrew_name} onChange={setF("hebrew_name")} />
          <input className="ls-input" style={{ marginTop: 10 }} placeholder="Relationship (e.g. spouse, child, parent)" value={familyForm.relationship} onChange={setF("relationship")} />
          <input className="ls-input" style={{ marginTop: 10 }} placeholder="Cell" value={familyForm.cell} onChange={setF("cell")} />
          <input className="ls-input" style={{ marginTop: 10 }} type="email" placeholder="Email" value={familyForm.email} onChange={setF("email")} />
          <input className="ls-input" style={{ marginTop: 10 }} placeholder="Date of death (if applicable)" value={familyForm.date_of_death} onChange={setF("date_of_death")} />
          <button onClick={saveFamily} className="ls-primary-btn" style={{ width: "100%", marginTop: 14 }}>{editingId ? "Update Family Member" : "Add Family Member"}</button>
          {editingId && <button onClick={() => { setEditingId(null); setFamilyForm(emptyFamily); }} className="ls-outline-btn" style={{ width: "100%", marginTop: 8 }}>Cancel Edit</button>}
        </div>
      </div>
    </div>
  );
}
